import { useState, useEffect, useCallback, useRef } from 'react';
import { hourlyFogForecastService } from '../../services/HourlyFogForecastService';
import { mapHourlyForecastToTimelineData, getAirportName } from '../../utils/HourlyForecastTimelineMapper';
import { HourlyForecastTimelineData, ErrorInfo } from '../../models/HourlyForecastTimelineData';
import { useDataRefresh } from '../useDataRefresh';

// Extended timeline data interface (same as MapPage)
interface ExtendedHourlyTimelineData extends HourlyForecastTimelineData {
  errors?: ErrorInfo[];
}

interface UseHourlyTimelineDataResult {
  timelineData: Record<number, ExtendedHourlyTimelineData>;
  loading: boolean;
  error: string | null;
  hasDataError: boolean;
  refresh: () => Promise<void>;
  lastUpdated: Date;
  refreshCount: number;
}

/**
 * Custom hook to fetch and manage hourly timeline data for multiple locations
 * Consolidates the hourly timeline fetching logic
 *
 * @param locationIds - Array of location IDs to fetch forecasts for
 * @param customNow - Optional custom "now" time for timeline generation
 * @param refreshInterval - Interval in minutes for auto-refresh (default: 5)
 * @returns Timeline data, loading state, error state, and refresh function
 */
export function useHourlyTimelineData(
  locationIds: number[],
  customNow?: Date | null,
  refreshInterval: number = 5
): UseHourlyTimelineDataResult {
  const [timelineData, setTimelineData] = useState<Record<number, ExtendedHourlyTimelineData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasDataError, setHasDataError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshCount, setRefreshCount] = useState(0);
  const isMounted = useRef(true);
  const firstChunkReceived = useRef(false);

  // Fetch forecast data for all locations
  const fetchForecastData = useCallback(async (isRefresh: boolean = false) => {
    // Skip fetching if no locations - only update state if needed to avoid re-renders
    if (locationIds.length === 0) {
      if (!isRefresh) {
        setLoading(false);
      }
      return;
    }

    // Only show loading and clear data on initial load, not on refresh
    if (!isRefresh) {
      setLoading(true);
      setTimelineData({});
      firstChunkReceived.current = false; // Reset for new fetch
    }
    setError(null);
    setHasDataError(false);

    try {

      // Fetch forecasts with progressive updates
      const result = await hourlyFogForecastService.getBatchHourlyFogForecast(
        locationIds,
        customNow || undefined,
        // Progressive callback - update UI as each chunk arrives
        (chunkForecasts) => {
          // Process this chunk and update state progressively
          const chunkData: Record<number, ExtendedHourlyTimelineData> = {};

          chunkForecasts.forEach((forecast, index) => {
            if (!forecast) {
              console.error(`No hourly forecast data for location at chunk index ${index}`);
              return;
            }

            // Extract location ID from the forecast data itself
            const locationId =
              forecast.hourly_buckets?.[0]?.location ??
              forecast.aggregated_actuals?.[0]?.location ??
              forecast.location;

            if (!locationId) {
              console.error(`Cannot determine location ID from hourly forecast at chunk index ${index}`);
              return;
            }

            // Validate this location was actually requested
            if (!locationIds.includes(locationId)) {
              console.warn(`Received unexpected location ${locationId} in batch response - skipping`);
              return;
            }

            // Map forecast to timeline data
            const mappedData = mapHourlyForecastToTimelineData(
              forecast,
              locationId,
              getAirportName(locationId)
            ) as ExtendedHourlyTimelineData;

            chunkData[locationId] = mappedData;
          });

          // Only update state if component is still mounted
          if (!isMounted.current) return;

          // Merge chunk data with existing data
          setTimelineData(prev => ({
            ...prev,
            ...chunkData
          }));

          // Hide loading spinner after first chunk (only on initial load)
          if (!firstChunkReceived.current && !isRefresh) {
            setLoading(false);
            firstChunkReceived.current = true;
          }
        }
      );

      // Only update state if component is still mounted
      if (!isMounted.current) return;

      setLastUpdated(new Date());

      // Set data error state if we had partial failures
      if (result.hasPartialFailure) {
        console.warn(`⚠️ Partial data failure: ${result.failedCount}/${result.requestedCount} locations failed to load`);
        setHasDataError(true);
      }

      // Check if we got any data (check after all chunks complete)
      setTimelineData(current => {
        if (Object.keys(current).length === 0) {
          setError('No hourly forecast data available');
        }
        return current;
      });
    } catch (err) {
      console.error('Error fetching hourly forecast data:', err);

      // Only update state if component is still mounted
      if (!isMounted.current) return;

      setError('Failed to load hourly forecast data');
      if (!isRefresh) {
        setLoading(false);
      }
    }
  }, [locationIds, customNow]);

  // Wrapper for auto-refresh (marks as refresh to avoid clearing data)
  const autoRefresh = useCallback(async () => {
    setRefreshCount(prev => prev + 1);
    await fetchForecastData(true);
  }, [fetchForecastData]);

  // Wrapper for manual refresh (also marks as refresh)
  const manualRefresh = useCallback(async () => {
    setRefreshCount(prev => prev + 1);
    await fetchForecastData(true);
  }, [fetchForecastData]);

  // Use data refresh hook for aligned refreshing
  // Pass customNow to disable auto-refresh when using custom time
  useDataRefresh(autoRefresh, refreshInterval, customNow);

  // Initial data fetch and refetch when locationIds or customNow changes
  useEffect(() => {
    fetchForecastData(false);
  }, [fetchForecastData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    timelineData,
    loading,
    error,
    hasDataError,
    refresh: manualRefresh,
    lastUpdated,
    refreshCount
  };
}
